import { v4 as cidrRegexV4, v6 as cidrRegexV6 } from 'cidr-regex';
import type { CIDRRule, InterfaceRule, LocalAllowRule } from '#dn-types/config-override.ts';

export const EMPTY_ERR = 'Enter a CIDR range or interface name';
export const DEFAULT_NETMASK_REQUIRED_v4_REMOTE_ERR =
  'Remote allow list requires allow or deny for 0.0.0.0/0 when using both allow and deny IPv4 rules';
export const DEFAULT_NETMASK_REQUIRED_v6_REMOTE_ERR =
  'Remote allow list requires allow or deny for ::/0 when using both allow and deny IPv6 rules';
export const DEFAULT_NETMASK_REQUIRED_v4_LOCAL_ERR =
  'Local allow list requires allow or deny for 0.0.0.0/0 when using both allow and deny IPv4 rules';
export const DEFAULT_NETMASK_REQUIRED_v6_LOCAL_ERR =
  'Local allow list requires allow or deny for ::/0 when using both allow and deny IPv6 rules';
export const MIXED_LOCAL_INTERFACE_ERR =
  'Local allow list interfaces must all be allowed or all denied, so the default can be the inverse';

/**
 * Return a list of ipv4 local allow rules
 */
export function filterIpV4(values: LocalAllowRule[]) {
  return values.filter((v): v is CIDRRule => 'cidr' in v && cidrRegexV4({ exact: true }).test(v.cidr));
}

/**
 * Return a list of ipv6 local allow rules
 */
export function filterIpV6(values: LocalAllowRule[]) {
  return values.filter((v): v is CIDRRule => 'cidr' in v && cidrRegexV6({ exact: true }).test(v.cidr));
}

/**
 * Return a list of ipv6 local allow rules
 */
export function filterInterface(values: LocalAllowRule[]) {
  return values.filter((v): v is InterfaceRule => 'interface' in v);
}

/**
 * Group ip ranges by allowed and denied rules
 */
export function getAllowDeny(ips: LocalAllowRule[]) {
  const hasAllows = ips.some((i) => i.allow);
  const hasDenies = ips.some((i) => !i.allow);
  return { hasAllows, hasDenies };
}

/**
 * Nebula requires that a default be specified when using a mix of allow and deny CIDR rules,
 * and does not allow a mix of allow & deny for interfaces.
 *
 * The rules provided to this function should not be a mix of cidr and interfaces, but rather
 * an array of one or the other.
 */
export function validAllowDeny(rules: CIDRRule[] | InterfaceRule[]) {
  if (rules.length <= 1) return true;

  const { hasAllows, hasDenies } = getAllowDeny(rules);
  if (hasAllows && hasDenies) {
    // CIDRs can only mix if one is a default
    if (!rules.some((i) => 'cidr' in i && i.cidr.endsWith('/0'))) {
      return false;
    }
    // Interfaces can never mix
    if (rules.some((i) => 'interface' in i)) {
      return false;
    }
  }

  return true;
}
